/**
 * AddPeopleToChatModal - Add users to create group from DM, or add to existing group (creator only).
 * For group add: prompts confirmation that new users can see all messages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { searchUsers } from '../helpers/searchHelpers';
import { createGroupConversation, addGroupParticipant } from '../helpers/messagesHelpers';
import ModalShell from './ModalShell';
import UserAvatar from '../components/Profile/UserAvatar';
import toast from 'react-hot-toast';

const SEARCH_DEBOUNCE_MS = 300;

function AddPeopleToChatModal({
  mode,
  currentUserId,
  otherUserId,
  groupConversation,
  onClose,
  onSuccess,
}) {
  const isGroupAdd = mode === 'group_add';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const participantIds = isGroupAdd
    ? new Set((groupConversation?.participants || []).map((p) => p.user_id))
    : new Set(otherUserId ? [otherUserId] : []);

  const doSearch = useCallback(async (q) => {
    if (!q?.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const users = await searchUsers(q.trim());
      setResults(Array.isArray(users) ? users : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const handleSelectUser = (user) => {
    if (user.user_id === currentUserId) return;
    if (participantIds.has(user.user_id)) {
      toast.error('User is already in the chat');
      return;
    }
    if (isGroupAdd) {
      setPendingUser(user);
      setShowConfirm(true);
    } else {
      handleAddUser(user);
    }
  };

  const handleConfirmAddToGroup = () => {
    if (pendingUser) {
      handleAddUser(pendingUser);
      setShowConfirm(false);
      setPendingUser(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingUser(null);
  };

  const handleAddUser = async (user) => {
    if (adding) return;
    setAdding(user.user_id);
    try {
      if (isGroupAdd) {
        const updated = await addGroupParticipant(groupConversation.group_conversation_id, user.user_id);
        toast.success(`Added ${user.displayname || user.username} to the group`);
        onClose?.();
        onSuccess?.(updated);
      } else {
        const participantIdsToCreate = otherUserId ? [otherUserId, user.user_id] : [user.user_id];
        const conv = await createGroupConversation(participantIdsToCreate);
        toast.success('Group chat created');
        onClose?.();
        onSuccess?.(conv);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(null);
    }
  };

  if (showConfirm && pendingUser) {
    return (
      <ModalShell
        title="Confirm add to group"
        onClose={handleCancelConfirm}
      >
        <div className="space-y-4">
          <p className="text-ash text-sm">
            New users will be able to see all messages in this chat. Add {pendingUser.displayname || pendingUser.username}?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancelConfirm}
              className="flex-1 py-3 rounded-xl border border-shadow text-ash font-medium hover:bg-shadow"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmAddToGroup}
              disabled={adding !== null}
              className="flex-1 py-3 rounded-xl bg-ether text-void font-medium disabled:opacity-50"
            >
              {adding === pendingUser.user_id ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  const title = isGroupAdd ? 'Add to group' : 'Add people to create group chat';

  return (
    <ModalShell title={title} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label htmlFor="add-people-search" className="block text-ash text-sm font-medium mb-1">
            Search users
          </label>
          <input
            id="add-people-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or username..."
            className="w-full px-4 py-3 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {searching ? (
            <p className="text-silver text-sm py-2">Searching...</p>
          ) : results.length === 0 ? (
            <p className="text-silver text-sm py-2">
              {query.trim() ? 'No users found' : 'Type to search users'}
            </p>
          ) : (
            <ul className="space-y-1">
              {results.map((u) => {
                const isParticipant = participantIds.has(u.user_id);
                const isSelf = u.user_id === currentUserId;
                const disabled = isParticipant || isSelf || adding !== null;
                return (
                  <li key={u.user_id}>
                    <button
                      type="button"
                      onClick={() => handleSelectUser(u)}
                      disabled={disabled}
                      className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <UserAvatar user={u} className="w-8 h-8 rounded-full" emojiClassName="text-sm" />
                      <span className="text-ash text-sm flex-1 truncate">
                        {u.displayname || u.username}
                        {isParticipant && ' (in chat)'}
                        {isSelf && ' (you)'}
                      </span>
                      {!isParticipant && !isSelf && (
                        <span className="text-ether text-xs font-medium">
                          {adding === u.user_id ? 'Adding...' : isGroupAdd ? 'Add' : 'Add'}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-xl border border-shadow text-ash font-medium hover:bg-shadow"
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

export default AddPeopleToChatModal;
